import { useNavigation } from '@react-navigation/native'
import { Platform } from 'react-native'
import { useCallback, useEffect, useRef } from 'react'

// Used for preventing double navigation.goBack() calls in one component
export const useGoBack = () => {
  const navigation = useNavigation()

  const invokedRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const goBack = useCallback(() => {
    if (invokedRef.current) {
      return
    }

    invokedRef.current = true

    // there might be race condition when goBack is called at the same time as navigating to another screen
    // in that case user would stuck on original screen
    // user should be able to go back again after 1 second
    timeoutRef.current = setTimeout(() => {
      invokedRef.current = false
    }, 1000)

    if (Platform.OS === 'android') {
      // On Android, wait one frame before closing so Fabric can finish the current UI update.
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null
        navigation.goBack()
      })
      return
    }

    return navigation.goBack()
  }, [navigation])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (animationFrameRef.current !== null) {
        // Prevent a queued Android goBack from firing after this hook unmounts.
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  return goBack
}
